// Dear Eden core: the shared window.DE namespace (config, events, focus trap, scroll lock, motion, money).
// Loaded first on every page as a module; the other modules run after it, in document order.
const root = document.documentElement;
root.classList.remove('no-js');
root.classList.add('js');

const DE = (window.DE = window.DE || {});

DE.config = (() => {
  try {
    return JSON.parse(document.getElementById('DE-config')?.textContent || '{}');
  } catch {
    return {};
  }
})();

// Events on document, so any module can listen without holding a reference to another.
DE.on = (type, handler, options) => {
  document.addEventListener(type, handler, options);
  return () => document.removeEventListener(type, handler, options);
};
DE.emit = (type, detail) => document.dispatchEvent(new CustomEvent(type, { detail }));

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
DE.motionOK = () => root.dataset.motion !== 'false' && !reducedMotion.matches;

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), ' +
  'textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

DE.focusable = (container) =>
  [...container.querySelectorAll(FOCUSABLE)].filter((el) => el.getClientRects().length && !el.closest('[inert]'));

// Keeps Tab inside `container` until the returned function is called.
DE.trapFocus = (container, initial) => {
  const onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const items = DE.focusable(container);
    if (!items.length) {
      event.preventDefault();
      container.focus();
      return;
    }
    const first = items[0];
    const last = items[items.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || !container.contains(active))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || !container.contains(active))) {
      event.preventDefault();
      first.focus();
    }
  };
  document.addEventListener('keydown', onKeydown);
  (initial || DE.focusable(container)[0] || container).focus({ preventScroll: true });
  return () => document.removeEventListener('keydown', onKeydown);
};

// Page scroll lock with a counter, so nested overlays release it only when the last one closes.
let locks = 0;
DE.lockScroll = () => {
  if (locks++ > 0) return;
  root.style.setProperty('--scrollbar-w', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
  root.classList.add('is-scroll-locked');
};
DE.unlockScroll = () => {
  if (locks === 0 || --locks > 0) return;
  root.classList.remove('is-scroll-locked');
  root.style.removeProperty('--scrollbar-w');
};

// Shopify money formats ({{amount}}, {{amount_no_decimals}}, ...), from cents.
DE.formatMoney = (cents, format = DE.config.money_format || '${{amount}}') => {
  const value = Number(cents) || 0;
  const group = (number, decimals, thousands = ',', decimal = '.') => {
    const [whole, fraction] = (number / 100).toFixed(decimals).split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
  };
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0);
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      case 'amount_no_decimals_with_space_separator':
        return group(value, 0, ' ');
      case 'amount_with_space_separator':
        return group(value, 2, ' ', ',');
      case 'amount_with_period_and_space_separator':
        return group(value, 2, ' ', '.');
      default:
        return group(value, 2);
    }
  });
};

// The whole percentage a compare-at price takes off a price (both in cents), or 0 when there is no real discount: no
// compare-at price, 0, equal to or below the price, or under 1% off once rounded. A price display shows the "Sale −X%"
// tag and the struck-through compare-at price only when this is above 0 (snippets/sale-percent.liquid, same rule).
DE.saleOff = (price, compare) => {
  const now = Number(price) || 0;
  const was = Number(compare) || 0;
  return was > now ? Math.round(((was - now) * 100) / was) : 0;
};

// Text for innerHTML templates.
DE.escape = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

// A Shopify CDN image URL at another width (?width= is resized by the CDN).
DE.imageUrl = (url, width) => {
  if (!url) return '';
  const [base, query = ''] = String(url).split('?');
  const params = new URLSearchParams(query);
  params.set('width', String(width));
  return `${base}?${params}`;
};

/* ---------- Sizes ---------- */
// Order from the "Size order" setting: listed values by position, combined sizes (XS/S) by the mean of their parts,
// unlisted numbers among the listed numbers by value, anything else last (alphabetical). snippets/size-sort.liquid
// applies the listed order in Liquid.
const listOf = (text) =>
  String(text || '')
    .split(/[,\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

DE.sizeNorm = (value) => {
  let v = String(value ?? '')
    .trim()
    .replace(/\s+/g, ' ')
    .toUpperCase()
    .replace(/\s*\/\s*/g, '/');
  if (/^(OS|ONE-SIZE|ONESIZE|FREE SIZE|FREESIZE)$/.test(v)) v = 'ONE SIZE';
  v = v.replace(/\bXXXL\b/g, '3XL');
  // "S-M" -> "S/M", "36-38" -> "36/38"
  return v.replace(/\b(\d+|\d?X*[SML])-(?=(\d+|\d?X*[SML])\b)/g, '$1/');
};

let sizeIndex = null;
const sizeTable = () => {
  if (sizeIndex) return sizeIndex;
  const map = new Map();
  const numbers = [];
  listOf(DE.config.size_order).forEach((item, i) => {
    const key = DE.sizeNorm(item);
    if (!map.has(key)) map.set(key, i);
    if (/^\d+(\.\d+)?$/.test(key)) numbers.push([Number(key), i]);
  });
  numbers.sort((a, b) => a[0] - b[0]);
  sizeIndex = { map, numbers };
  return sizeIndex;
};

const UNLISTED = 10000;
DE.sizeRank = (value) => {
  const { map, numbers } = sizeTable();
  const key = DE.sizeNorm(value);
  if (map.has(key)) return map.get(key);
  const parts = key.split('/');
  if (parts.length > 1 && parts.every((part) => map.has(part))) {
    return parts.reduce((sum, part) => sum + map.get(part), 0) / parts.length + 0.01;
  }
  if (/^\d+(\.\d+)?$/.test(key)) {
    const n = Number(key);
    if (!numbers.length) return UNLISTED - 1;
    let rank = numbers[0][1] - 0.5;
    for (const [listed, i] of numbers) if (listed <= n) rank = i + 0.5;
    return rank;
  }
  return UNLISTED;
};

DE.sortSizes = (values) =>
  [...values].sort((a, b) => {
    const diff = DE.sizeRank(a) - DE.sizeRank(b);
    if (diff) return diff;
    return DE.sizeRank(a) >= UNLISTED ? String(a).localeCompare(String(b)) : 0;
  });

// A size chip matches a product size when they're equal, or (setting on) when the chip is a part of a combined
// size: S and XS match XS/S, L and XL match L/XL. Numbers match exactly.
DE.sizeMatches = (chip, size) => {
  const c = DE.sizeNorm(chip);
  const s = DE.sizeNorm(size);
  if (c === s) return true;
  return DE.config.size_combined_matches !== false && s.split('/').includes(c);
};

/* ---------- Deferred images ---------- */
// For images rendered with priority 'defer' (data-src, data-srcset, data-sizes): sets the real attributes once.
DE.hydrate = (img) => {
  if (!img || !img.dataset || img.dataset.hydrated) return img;
  const { src, srcset, sizes } = img.dataset;
  if (sizes) img.sizes = sizes;
  if (srcset) img.srcset = srcset;
  if (src) img.src = src;
  img.dataset.hydrated = 'true';
  return img;
};

/* ---------- <lazy-section data-url="..."> ---------- */
// Fetches a section rendered on request (data-url includes section_id, or add data-section-id) when it comes within
// ~600px of the viewport, and swaps its content in: the elements matching data-select, else everything inside the
// section wrapper. Then dispatches "lazy-section:loaded" (bubbles; detail { element }) so modules can bind.
// On failure: data-state="error" and "lazy-section:error"; the fallback content stays.
class LazySection extends HTMLElement {
  connectedCallback() {
    if (this.observer || this.dataset.state) return;
    if (!('IntersectionObserver' in window)) {
      this.load();
      return;
    }
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.load();
      },
      { rootMargin: this.dataset.margin || '600px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.observer = null;
  }

  url() {
    const url = new URL(this.dataset.url, window.location.href);
    if (this.dataset.sectionId && !url.searchParams.has('section_id')) url.searchParams.set('section_id', this.dataset.sectionId);
    return url;
  }

  async load() {
    if (this.dataset.state) return;
    this.dataset.state = 'loading';
    try {
      const response = await fetch(this.url(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const wrapper = html.querySelector('.shopify-section') || html.body;
      const nodes = this.dataset.select ? [...wrapper.querySelectorAll(this.dataset.select)] : [...wrapper.childNodes];
      this.replaceChildren(...nodes.map((node) => document.importNode(node, true)));
      this.dataset.state = 'loaded';
      this.dispatchEvent(new CustomEvent('lazy-section:loaded', { bubbles: true, detail: { element: this } }));
    } catch (error) {
      this.dataset.state = 'error';
      this.dispatchEvent(new CustomEvent('lazy-section:error', { bubbles: true, detail: { element: this, error } }));
    }
  }
}

if (!customElements.get('lazy-section')) customElements.define('lazy-section', LazySection);
